<template>
   <Modal
      :dialog.sync="dialog"
      :contentClass="'esh-theme adminModal'"
      :title="title"
    >
    <template #body>
      <v-card outlined>
        <v-card-text>
          <v-row no-gutters>
            <v-col md="4">
              <v-text-field
                v-model="filter.search"
                placeholder="Название тарифа"
              />
            </v-col>
              <v-col>
              <v-row justify="end">
                <v-col md="5">
                  <Select
                    label="Муниципалитет:"
                    v-model="filter.municipality"
                    :items="municipalities"
                    item-text="short_name"
                    item-value="id"
                    class="fs-admin-complex-autocomplete"
                    @change="changeDistrict($event)"
                    flat
                    dense
                    blank
                  />
                </v-col>
                <v-col md="5">
                  <Select
                    label="Школа:"
                    v-model="filter.unit"
                    :items="schools"
                    item-text="short_name"
                    item-value="id"
                    class="fs-admin-complex-autocomplete"
                    @change="getDataFromFirstPage"
                    flat
                    dense
                    blank
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <Table
            :headers="headers"
            :items="tariffs"
            :loading="loading"
            :items-per-page="tariffsPerPage"
            :server-items-length="totalTariffs"
            :page="page"
            @update:request="updateRequest"
            item-key="id"
            class="elevation-1"
            disable-sort
          >
            <template v-slot:item="{ item }">
              <tr :class="{'success': item.id === selectedTariff}">
                <td>{{ item.created_at | dateTime }}</td>
                <td>{{ item.name }}</td>
                <td>
                  <v-btn
                    :disabled="item.id === excludeId"
                    @click="toggleTariff(item)"
                  >
                    {{ item.id === selectedTariff ? 'Отмена' : 'Выбрать' }}
                  </v-btn>
                </td>
              </tr>
            </template>
          </Table>
        </v-card-text>
      </v-card>
    </template>
    <template #actions>
      <v-row justify="end" no-gutters>
        <v-btn
          :disabled="selectedTariff === null"
          @click="selectTariff"
        >
          Дальше
        </v-btn>
      </v-row>
    </template>
  </Modal>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import { BASE } from '@/config'
import { Table, Select, Modal } from '@/components/common'
import {
  GET_TARIFFS,
  GET_SUGGESTION_MUNICIPALITIES,
  GET_SUGGESTION_SCHOOLS
} from '@/requests/superadmin'

const initFilterState = {
  search: '',
  unit: null,
  municipality: null
}

export default {
  name: 'TariffFindModalSuperAdmin',
  components: {
    Table,
    Select,
    Modal
  },
  props: {
    show: {
      props: Boolean,
      default: false
    },
    excludeId: {
      props: Number,
      default: -1
    },
    title: {
      props: String,
      default: 'Поиск тарифа'
    }
  },
  data() {
    return {
      headers: [
        { text: 'Дата создания', value: 'created_at' },
        { text: 'Название', value: 'name', sortable: false },
        { text: '', value: 'options', sortable: false }
      ],
      loading: false,
      municipalities: [],
      schools: [],
      tariffs: [],
      totalTariffs: 0,
      page: 1,
      tariffsPerPage: BASE.LIMIT,
      selectedTariff: null,
      filter: { ...initFilterState }
    }
  },
  mounted() {
    this.loadMunicipalities()
  },
  computed: {
    ...mapState({
      ACTIVE: state => state.common.constants.tariff_statuses.find(s => s.alias === 'ACTIVE')
    }),
    dialog: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      }
    }
  },
  watch: {
    show: function(state) {
      if (state) {
        this.init()
      }
    },
    'filter.search': function(state, prevState) {
      if (
        state.length > prevState.length &&
        state.length >= BASE.SEARCH_MIN_LETTERS
      ) {
        this.searchTariffs()
      } else if (
        state.length < prevState.length &&
        prevState.length >= BASE.SEARCH_MIN_LETTERS
      ) {
        this.searchTariffs()
      }
    }
  },
  methods: {
    init() {
      this.selectedTariff = null
      this.filter = { ...initFilterState }
      this.getDataFromFirstPage()
    },
    toggleTariff(tariff) {
      this.selectedTariff = tariff.id === this.selectedTariff ? null : tariff.id
    },
    selectTariff() {
      const tariff = this.tariffs.find(t => t.id === this.selectedTariff)
      this.$emit('founded', tariff)
      this.init()
    },
    searchTariffs: debounce(function() {
      this.getDataFromFirstPage()
    }, 300),
    updateRequest({ page, itemsPerPage, offset }) {
      this.tariffsPerPage = itemsPerPage
      this.page = page
      this.getData({ offset })
    },
    getDataFromFirstPage() {
      this.page = 1
      this.getData()
    },
    changeDistrict(districtId) {
      this.filter.unit = null
      this.schools = []
  
      if (districtId !== null) {
        this.loadDistrictSchools(districtId)
      }

      this.getDataFromFirstPage()
    },
    async getData(params = {}) {
      try {
        this.loading = true
        const { data: { results, count } } = await GET_TARIFFS({
          limit: this.tariffsPerPage,
          name: this.filter.search.length >= BASE.SEARCH_MIN_LETTERS ? this.filter.search : '',
          unit: this.filter.unit,
          status: this.ACTIVE.id,
          ...params
        })

        this.totalTariffs = count
        this.tariffs = results
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: 'Не удалось загрузить информацию о тарифах'
        })
      } finally {
        this.loading = false
      }
    },
    async loadDistrictSchools(municipalityId) {
      try {
        const { data: { results } } = await GET_SUGGESTION_SCHOOLS({
          district: municipalityId
        })

        this.schools = results
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Не удалось получить список школ муниципалитета'
        })
      }
    },
    async loadMunicipalities() {
      try {
        const { data: { results } } = await GET_SUGGESTION_MUNICIPALITIES()
        this.municipalities = results
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: 'Не удалось получить список муниципалитетов'
        })
      }
    }
  }
}
</script>